import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { Zap, Eye, EyeOff, Check } from 'lucide-react';
import './Auth.css';

export default function RegisterPage() {
  const [form, setForm]       = useState({ username:'', email:'', password:'', displayName:'' });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const toast        = useToast();
  const navigate     = useNavigate();

  const pw = form.password;
  const strength = pw.length === 0 ? 0 : pw.length < 6 ? 1 : pw.length < 10 ? 2 : 3;
  const strengthLabel = ['', 'Weak', 'Good', 'Strong'];
  const strengthColor = ['', '#FF4D4D', '#FFB830', '#00D68F'];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setLoading(true);
    try {
      await register(form);
      navigate('/home');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const change = f => e => setForm(p => ({ ...p, [f]: e.target.value }));

  return (
    <div className="auth-page">
      <div className="auth-left">
        <div className="auth-left-inner">
          <div className="auth-brand">
            <div className="auth-logo"><Zap size={26}/></div>
            <span>Nexa</span>
          </div>
          <h1 className="auth-headline">Join thousands already on Nexa</h1>
          <p className="auth-desc">Your all-in-one communication platform. Free forever for individuals.</p>
          <div className="auth-checklist">
            {['Private encrypted chats','Voice & video calls','AI that reads your messages so you don\'t have to','Communities & public feed'].map(item => (
              <div key={item} className="auth-check-item">
                <div className="auth-check-icon"><Check size={13}/></div>
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="auth-left-glow g1"/>
        <div className="auth-left-glow g2"/>
      </div>

      <div className="auth-right">
        <div className="auth-card">
          <div className="auth-card-header">
            <div className="auth-logo-sm"><Zap size={18}/></div>
            <h2>Create account</h2>
            <p>Get started for free</p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Full Name</label>
              <input placeholder="Your name" value={form.displayName}
                onChange={change('displayName')} required autoFocus />
            </div>
            <div className="form-group">
              <label>Username</label>
              <input placeholder="@username" value={form.username}
                onChange={change('username')} required
                minLength={3} maxLength={30}
                pattern="[a-zA-Z0-9_]+" title="Letters, numbers and underscores only" />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input type="email" placeholder="you@example.com"
                value={form.email} onChange={change('email')} required />
            </div>
            <div className="form-group">
              <label>Password</label>
              <div style={{ position: 'relative' }}>
                <input type={showPass ? 'text' : 'password'}
                  placeholder="At least 6 characters"
                  value={form.password} onChange={change('password')}
                  required minLength={6} style={{ paddingRight: 42 }} />
                <button type="button" className="pass-toggle"
                  onClick={() => setShowPass(v => !v)}>
                  {showPass ? <EyeOff size={16}/> : <Eye size={16}/>}
                </button>
              </div>
              {pw.length > 0 && (
                <div className="pw-strength">
                  <div className="pw-strength-bar">
                    {[1,2,3].map(i => (
                      <div key={i} className="pw-bar-seg"
                        style={{ background: i <= strength ? strengthColor[strength] : 'var(--border)' }} />
                    ))}
                  </div>
                  <span style={{ color: strengthColor[strength], fontSize: '0.72rem' }}>
                    {strengthLabel[strength]}
                  </span>
                </div>
              )}
            </div>
            <button type="submit" className="btn btn-primary w-full auth-submit" disabled={loading}>
              {loading ? <span className="loader" style={{ width:18, height:18, borderWidth:2 }}/> : 'Create Account'}
            </button>
          </form>

          <p className="auth-footer-link">
            Already have an account? <Link to="/login">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
